import { useMemo, useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import "./Resources.scss";

import heroImage from "./assets/resources-hero.webp";
import latestBabyImage from "./assets/images/baby-outdoor-fall-portrait.webp";
import latestFamilyImage from "./assets/images/family-summer-park-walk-portrait.webp";
import latestMaternityImage from "./assets/images/maternity-outdoor-fall-red-gown investment.webp";
import latestNewbornImage from "./assets/images/newborn-pink-studio-portrait.webp";
import { ArticleCard } from "./components/ArticleCard";
import { ArrowIcon } from "./components/ArrowIcon";
import { CategoryCard } from "./components/CategoryCard";
import { FaqAccordion } from "./components/FaqAccordion";
import { categories, faqs } from "./data/resources.data";
import { resourceArticles } from "./components/newborn-pages/data/resourceArticles";
import type { ResourceFeatureSeason } from "./components/newborn-pages/data/resourceArticles";
import { ResourceIndexSeo } from "./components/newborn-pages/ResourceSeo";
import type { ResourceArticle as ResourceArticleCardData } from "./types";

interface SearchResult {
  title: string;
  eyebrow: string;
  href: string;
  searchText: string;
  normalizedTitle: string;
}

const normalizeSearchText = (value: string): string =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const activeCategoryIds = new Set(resourceArticles.map((article) => article.category));
const activeCategories = categories.filter((category) => activeCategoryIds.has(category.id));

const latestGuideImages = {
  newborn: {
    src: latestNewbornImage,
    alt: "Newborn baby posed in a soft pink studio setup",
  },
  baby: {
    src: latestBabyImage,
    alt: "Baby photographed outdoors among colorful autumn leaves",
  },
  maternity: {
    src: latestMaternityImage,
    alt: "Expectant mother wearing a red gown during an outdoor maternity session",
  },
  family: {
    src: latestFamilyImage,
    alt: "Family walking together during a summer park portrait session",
  },
} as const;

const toArticleCardData = (article: (typeof resourceArticles)[number]): ResourceArticleCardData => {
  const guideImage =
    article.category in latestGuideImages
      ? latestGuideImages[article.category as keyof typeof latestGuideImages]
      : undefined;

  return {
    title: article.title,
    eyebrow: article.eyebrow,
    description: article.description,
    href: `/resources/${article.category}/${article.slug}/`,
    imageSrc: article.image ?? guideImage?.src,
    imageAlt: article.imageAlt ?? guideImage?.alt,
    publishedAt: new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${article.publishedAt}T12:00:00`)),
    readTime: article.readTime,
  };
};

const articlesByNewest = [...resourceArticles].sort(
  (first, second) => new Date(second.publishedAt).getTime() - new Date(first.publishedAt).getTime()
);

const getCurrentFeatureSeason = (month: number): ResourceFeatureSeason => {
  if (month >= 9 || month === 0) return "winter-holidays";
  if (month >= 1 && month <= 3) return "spring";
  if (month >= 4 && month <= 6) return "summer";
  return "fall";
};

const defaultFeaturedArticle =
  resourceArticles.find(
    (article) => article.category === "maternity" && article.slug === "when-is-the-best-time-to-take-maternity-photos"
  ) ??
  resourceArticles.find((article) => article.category === "newborn") ??
  articlesByNewest[0];

const currentFeatureSeason = getCurrentFeatureSeason(new Date().getMonth());
const seasonalFeaturedArticle = articlesByNewest.find((article) =>
  article.featuredSeasons?.includes(currentFeatureSeason)
);
const featuredResourceArticle = seasonalFeaturedArticle ?? defaultFeaturedArticle;

// Keep the feature paired with one recent guide from each core session category.
const latestResourceArticles = (() => {
  const selected = featuredResourceArticle ? [featuredResourceArticle] : [];
  const supportingCategories = ["baby", "newborn", "family", "maternity"] as const;

  supportingCategories.forEach((category) => {
    if (selected.length >= 4 || selected.some((article) => article.category === category)) return;

    const article = articlesByNewest.find((candidate) => candidate.category === category);
    if (article) selected.push(article);
  });

  return selected.map(toArticleCardData);
})();

const latestCharlotteGuides = articlesByNewest
  .filter((article) => article.category === "charlotte")
  .slice(0, 3)
  .map(toArticleCardData);

const searchIndex: SearchResult[] = [
  ...activeCategories.map((category) => ({
    title: category.title,
    eyebrow: "CATEGORY",
    href: category.href,
    searchText: normalizeSearchText(`${category.title} ${category.description}`),
    normalizedTitle: normalizeSearchText(category.title),
  })),
  ...resourceArticles.map((article) => ({
    title: article.title,
    eyebrow: article.category.toUpperCase(),
    href: `/resources/${article.category}/${article.slug}/`,
    searchText: normalizeSearchText(`${article.title} ${article.category} ${article.description}`),
    normalizedTitle: normalizeSearchText(article.title),
  })),
  ...faqs.map((faq) => ({
    title: faq.question,
    eyebrow: "FAQ",
    href: faq.href,
    searchText: normalizeSearchText(`${faq.question} ${faq.answer}`),
    normalizedTitle: normalizeSearchText(faq.question),
  })),
];

export const Resources = (): ReactElement => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const normalizedQuery = normalizeSearchText(query);

  const searchResults = useMemo(() => {
    if (!normalizedQuery) return [];

    const queryWords = normalizedQuery.split(" ").filter(Boolean);

    return searchIndex
      .filter((item) => queryWords.every((word) => item.searchText.includes(word)))
      .sort((first, second) => {
        const firstStartsWithQuery = first.normalizedTitle.startsWith(normalizedQuery) ? 1 : 0;
        const secondStartsWithQuery = second.normalizedTitle.startsWith(normalizedQuery) ? 1 : 0;

        if (firstStartsWithQuery !== secondStartsWithQuery) return secondStartsWithQuery - firstStartsWithQuery;

        const firstTitleMatches = queryWords.filter((word) => first.normalizedTitle.includes(word)).length;
        const secondTitleMatches = queryWords.filter((word) => second.normalizedTitle.includes(word)).length;

        return secondTitleMatches - firstTitleMatches;
      })
      .slice(0, 6);
  }, [normalizedQuery]);

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (searchResults[0]) {
      navigate(searchResults[0].href);
      return;
    }

    setIsSearchOpen(true);
  };

  return (
    <main className="resources-page">
      <ResourceIndexSeo />
      <section className="resources-hero">
        <img
          alt="Sleeping newborn baby wrapped in a soft cream blanket"
          className="resources-hero__image"
          src={heroImage}
        />

        <div className="resources-shell resources-hero__content">
          <div className="resources-hero__copy">
            <h1>Resources</h1>

            <p>
              Helpful answers for the questions that come up before a session — from choosing the right time to feeling
              prepared on the day.
            </p>

            <div className="resources-search-wrap">
              <form className="resources-search" onSubmit={handleSearchSubmit} role="search">
                <label className="sr-only" htmlFor="resources-search">
                  Search resources
                </label>

                <input
                  id="resources-search"
                  onChange={(event) => setQuery(event.target.value)}
                  onFocus={() => setIsSearchOpen(true)}
                  placeholder="Search guides and articles..."
                  type="search"
                  value={query}
                />

                <button aria-label="Search resources" type="submit">
                  <svg aria-hidden="true" viewBox="0 0 24 24">
                    <circle cx="10.8" cy="10.8" r="5.8" />
                    <path d="m15.2 15.2 4 4" />
                  </svg>
                </button>
              </form>

              {isSearchOpen && normalizedQuery && (
                <div className="resources-search-results" role="region" aria-label="Search results">
                  {searchResults.length > 0 ? (
                    <ul>
                      {searchResults.map((result) => (
                        <li key={result.href}>
                          <a href={result.href} onClick={() => setIsSearchOpen(false)}>
                            <span>{result.eyebrow}</span>
                            {result.title}
                            <ArrowIcon size={14} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No guides found for “{query.trim()}”.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="resources-section resources-section--categories">
        <div className="resources-shell">
          <header className="resources-section-heading resources-section-heading--library">
            <div>
              <p className="resources-eyebrow">EXPLORE THE LIBRARY</p>
              <h2>Guidance for every chapter</h2>
            </div>
            <div className="resources-library-note">
              <strong>{activeCategories.length} collections</strong>
              <span>{resourceArticles.length} practical guides, written to help you plan with confidence.</span>
            </div>
          </header>
          <div className="resources-category-grid">
            {activeCategories.map((category) => (
              <CategoryCard category={category} key={category.id} />
            ))}
          </div>
        </div>
      </section>

      <section className="resources-section resources-section--latest">
        <div className="resources-shell">
          <header className="resources-section-heading resources-section-heading--row">
            <div>
              <p className="resources-eyebrow">LATEST GUIDES</p>
              <h2>Featured and recently added</h2>
            </div>
            <p className="resources-latest-intro">
              A seasonal favorite, followed by the newest practical guides from across the library.
            </p>
          </header>
          <div className="resources-latest-grid">
            {latestResourceArticles.map((article) => (
              <ArticleCard article={article} key={article.href} />
            ))}
          </div>
        </div>
      </section>

      {latestCharlotteGuides.length > 0 && (
        <section className="resources-section resources-section--charlotte">
          <div className="resources-shell">
            <header className="resources-section-heading resources-section-heading--row">
              <div>
                <p className="resources-eyebrow">LOCAL FAVORITES</p>
                <h2>Charlotte resources</h2>
              </div>
            </header>
            <div className="resources-article-grid">
              {latestCharlotteGuides.map((article) => (
                <ArticleCard article={article} key={article.href} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="resources-faq">
        <div className="resources-shell resources-faq__layout">
          <div className="resources-faq__intro">
            <p className="resources-eyebrow">HELPFUL ANSWERS</p>
            <h2>
              Frequently asked <em>questions.</em>
            </h2>
            <p>
              Short answers to the questions families ask most often. For more detail, each answer leads to a complete
              guide.
            </p>
          </div>
          <FaqAccordion items={faqs} />
        </div>
      </section>

      <section className="resources-cta">
        <div className="resources-shell resources-cta__inner">
          <p className="resources-eyebrow">LET&apos;S CREATE SOMETHING BEAUTIFUL</p>
          <h2>Ready to capture these memories?</h2>
          <p>Let&apos;s create beautiful images your family will treasure forever.</p>
          <a className="resources-button" href="/contact/">
            Contact Alena <ArrowIcon />
          </a>
        </div>
      </section>
    </main>
  );
};

export default Resources;
